import {
  Mate,
  MateModule,
  Module,
  ModuleEntityType,
  ModuleOrder,
  ModuleQuestion,
  Worldview,
} from "@/types/entity";
import { ModuleFieldItem } from "../module";
import i18n, { getLocaleIdByLocale } from "@/locales";
import { PERSONA_MODULE_ID } from "./moduleMate";

export const SETTING_MODULE_ID = -9;
class ModuleBase {
  // 实现基础属性
  entity: Mate | Worldview;
  entityType: ModuleEntityType = "mate";
  mateModuleList: MateModule[];
  moduleList: Module[];
  moduleFieldsMap: { [moduleId: number]: ModuleFieldItem[] } = {};
  moduleIdMap: { [moduleId: number]: Module } = {};
  moduleIdMateModuleMap: Record<number, MateModule>;
  moduleQuestionMap: Record<number, ModuleQuestion[]>;

  constructor(
    entity: Mate | Worldview,
    entityType: ModuleEntityType,
    mateModuleList: MateModule[],
    moduleQuestionMap: Record<number, ModuleQuestion[]>
  ) {
    this.entity = entity;
    this.entityType = entityType;
    this.entity.moduleOrder = this.entity.moduleOrder || [];
    this.mateModuleList = mateModuleList;
    this.moduleList = mateModuleList.map(({ module }) => {
      this.moduleIdMap[module.id] = module;
      return module;
    });
    this.moduleQuestionMap = moduleQuestionMap;

    this.moduleIdMateModuleMap = mateModuleList.reduce((acc, cur) => {
      acc[cur.moduleId] = cur;
      return acc;
    }, {} as Record<number, MateModule>);

    this.buildModuleFieldMap(
      this.moduleQuestionMap,
      this.moduleIdMateModuleMap
    );
  }

  updateEntityOrder(): Promise<void> {
    throw new Error("Method not implemented.");
  }

  // 统一的模块

  initModuleList(): void {
    this.moduleList.forEach((module) => {
      module.name =
        module.multiLangzz?.find(
          (lang) =>
            lang.languageId === getLocaleIdByLocale(i18n.global.locale.value)
        )?.text || module.name;
    });
    // 根据 this.moduleOrder 来重新排序
    this.sortModule();
  }
  initMateModuleOrder(): void {
    // 初始化的状态
    if (
      !this.entity.moduleOrder ||
      this.entity.moduleOrder.length === 0 ||
      typeof this.entity.moduleOrder[0] === "number"
    ) {
      const children = this.moduleList.map((v) => v.id);
      // 1377 再第一个，然后按照从小到大顺序排列

      children.sort((a, b) => {
        if (a === PERSONA_MODULE_ID) return -1;
        if (b === PERSONA_MODULE_ID) return 1;
        return a - b;
      });

      this.entity.moduleOrder = [
        {
          id: 0,
          children,
        },
      ];
      this.updateEntityOrder();
    }

    // 处理模块不同步
    this.moduleList.forEach((module) => {
      if (!this.entity.moduleOrder[0].children.includes(module.id)) {
        if (module.id === PERSONA_MODULE_ID) {
          this.entity.moduleOrder[0].children.unshift(module.id);
        } else {
          this.entity.moduleOrder[0].children.push(module.id);
        }
      }

      const moduleOrder = this.entity.moduleOrder.find(
        (v) => v.id === module.id
      );

      if (!moduleOrder) {
        this.entity.moduleOrder.push({
          id: module.id,
          children: this.moduleFieldsMap[module.id]?.map((v) => v.id) || [],
        });
      }
      if (
        moduleOrder &&
        moduleOrder.children?.length !== this.moduleFieldsMap[module.id]?.length
      ) {
        moduleOrder.children =
          this.moduleFieldsMap[module.id]?.map((v) => v.id) || [];
      }
    });

    // 去重
    const { children } = this.entity.moduleOrder[0];
    const uniqueChildren = children?.filter(
      (v: number, index: number, self: number[]) => self.indexOf(v) === index
    );
    if (uniqueChildren.length !== children.length) {
      this.entity.moduleOrder[0].children = uniqueChildren;
      this.updateEntityOrder();
    }

    // 处理模块不同步
    let isChanged = false;
    const moduleIds = this.mateModuleList.map((v) => v.moduleId);
    const ordersMap: Record<number, boolean> = {};
    const basicInfoOrder = this.entity.moduleOrder[0];
    basicInfoOrder.children?.forEach((moduleId: number) => {
      // 如果模块不存在，则删除
      if (!this.moduleIdMap[moduleId]) {
        basicInfoOrder.children = basicInfoOrder.children.filter(
          (v) => v !== moduleId
        );
        isChanged = true;
        return;
      }
      ordersMap[Number(moduleId)] = true;
    });

    // 新增模块
    for (const moduleId of moduleIds) {
      if (!ordersMap[moduleId]) {
        if (this.moduleIdMateModuleMap[moduleId]) {
          const _answerMap = this.moduleIdMateModuleMap[moduleId].answerMap;
          this.addMateModule(
            this.moduleIdMateModuleMap[moduleId],
            Object.keys(_answerMap).map(Number),
            true
          );
          isChanged = true;
        }
      }
    }
    if (isChanged) {
      this.updateEntityOrder();
    }
  }

  getMateModuleOrderById(moduleId: number): ModuleOrder | null {
    if (moduleId === 0) return this.entity.moduleOrder[0] || null;
    const moduleOrder = this.entity.moduleOrder?.find((v) => v.id === moduleId);
    if (!moduleOrder) {
      if (moduleId === 1) {
        return {
          id: 0,
          children: [],
        };
      }
      return null;
    }
    return moduleOrder;
  }

  async updateMateModuleOrder(id: number, children: number[]): Promise<void> {
    const moduleOrder =
      id === 0
        ? this.entity.moduleOrder[0]
        : this.entity.moduleOrder.find((v) => v.id === id);
    if (!moduleOrder) return;
    moduleOrder.children = children;
    return this.updateEntityOrder();
  }

  async deleteMateModule(
    moduleId: number,
    moduleChildId?: number
  ): Promise<void> {
    if (moduleChildId) {
      const moduleOrder = this.entity.moduleOrder.find(
        (v) => v.id === moduleId
      );
      if (!moduleOrder) return;
      moduleOrder.children = moduleOrder.children.filter(
        (id: number) => id !== moduleChildId
      );
    } else {
      const basicInfoOrder = this.entity.moduleOrder[0];
      if (basicInfoOrder)
        basicInfoOrder.children = basicInfoOrder.children.filter(
          (id) => id !== moduleId
        );

      const idx = this.entity.moduleOrder.findIndex((v) => v.id === moduleId);
      if (idx !== -1) this.entity.moduleOrder.splice(idx, 1);
    }

    await this.updateEntityOrder();
  }

  sortModule() {
    const moduleOrder = this.entity.moduleOrder[0];
    if (!moduleOrder) return;
    this.moduleList = this.moduleList.sort((a, b) => {
      if (a.id === PERSONA_MODULE_ID) return -1;
      if (b.id === PERSONA_MODULE_ID) return 1;

      const orderA = moduleOrder.children.indexOf(a.id);
      const orderB = moduleOrder.children.indexOf(b.id);
      return orderA - orderB;
    });
  }

  async addMateModule(
    mateModule: MateModule,
    children: number[] = [],
    withoutSave = false
  ): Promise<void> {
    if (this.entity.moduleOrder.find((v) => v.id === mateModule.moduleId))
      return;

    this.entity.moduleOrder.push({
      id: mateModule.moduleId,
      children,
    });
    const basicInfoOrder = this.getMateModuleOrderById(0);
    if (
      basicInfoOrder &&
      !basicInfoOrder.children?.includes(mateModule.moduleId)
    ) {
      basicInfoOrder.children?.push(mateModule.moduleId);
    }

    if (!withoutSave) {
      this.moduleIdMateModuleMap[mateModule.moduleId] = mateModule;
      this.moduleIdMap[mateModule.moduleId] = mateModule.module;
      this.mateModuleList.push(mateModule);
      this.moduleList.push(mateModule.module);
      this.moduleFieldsMap[mateModule.moduleId] = [];
      await this.updateEntityOrder();
    }
  }

  async addMateModuleQuestion(
    moduleId: number,
    questionId: number
  ): Promise<void> {
    const moduleOrder = this.entity.moduleOrder.find((v) => v.id === moduleId);
    if (!moduleOrder) return;
    if (moduleOrder.children.includes(questionId)) {
      return;
    }
    moduleOrder.children.push(questionId);
    await this.updateEntityOrder();
  }

  async deleteMateModuleQuestion(questionId: number): Promise<void> {
    const moduleOrder = this.entity.moduleOrder.find(
      (v) => v.id === this.entity.id
    );
    if (!moduleOrder) return;
    if (!moduleOrder.children.includes(questionId)) {
      return;
    }
    moduleOrder.children = moduleOrder.children.filter(
      (id) => id !== questionId
    );
    await this.updateEntityOrder();
  }

  async buildModuleFieldMap(
    questions: Record<number, ModuleQuestion[]>,
    answers: Record<number, MateModule>
  ) {
    const answerMap: Record<number, Record<number, string>> = {};
    Object.entries(answers).reduce((acc, [moduleId, mateModule]) => {
      acc[Number(moduleId)] = mateModule.answerMap;
      return acc;
    }, answerMap);

    const questionAndAnswerMap: { [moduleId: number]: ModuleFieldItem[] } = {};
    Object.entries(questions).reduce((acc, [moduleId, questions]) => {
      acc[Number(moduleId)] = questions.map((question: ModuleQuestion) => {
        const field = this.convertQuestionToField(question);
        if (answerMap[Number(moduleId)]) {
          field.value = answerMap[Number(moduleId)][question.id];
        }
        return field;
      });
      return acc;
    }, questionAndAnswerMap);
    this.moduleFieldsMap = questionAndAnswerMap;
  }

  convertQuestionToField(question: ModuleQuestion): ModuleFieldItem {
    const locale = i18n.global.locale;
    const localeId = getLocaleIdByLocale(locale.value);
    return {
      ...question,
      value: "",
      displayName:
        question.multiLangzz?.find((lang) => lang.languageId === localeId)
          ?.text || question.text,
    } as ModuleFieldItem;
  }
}

export default ModuleBase;
